import {
  Badge,
  Box,
  Divider,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
  VStack,
} from '@elkaso-app/web-design';
import { useGetMappingByIdApi } from 'apis/crm-brand-trade-license-mappings';
import React from 'react';

interface ViewMappingModalProps {
  isOpen: boolean;
  onClose: () => void;
  mappingId: number;
}

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
  isLoading?: boolean;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value, isLoading }) => (
  <VStack align='start' spacing={1}>
    <Text fontSize='sm' fontWeight='semibold' color='gray.600'>
      {label}
    </Text>
    {isLoading ? (
      <Skeleton height='20px' width='200px' />
    ) : (
      <Text fontSize='md' color='gray.800'>
        {value || 'N/A'}
      </Text>
    )}
  </VStack>
);

export const ViewMappingModal: React.FC<ViewMappingModalProps> = ({ isOpen, onClose, mappingId }) => {
  const { data: mappingDetails, isLoading } = useGetMappingByIdApi(mappingId, {
    enabled: isOpen && !!mappingId,
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Trade License Mapping Details</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align='stretch'>
            {/* Basic Information */}
            <Box>
              <Text fontSize='lg' fontWeight='bold' mb={3} color='blue.600'>
                Talabat Trade License Information
              </Text>
              <VStack spacing={3} align='stretch'>
                <DetailRow label='Brand Name' value={mappingDetails?.entityName} isLoading={isLoading} />
                <DetailRow label='Legal Name' value={mappingDetails?.tradeLicenseName} isLoading={isLoading} />
              </VStack>
            </Box>

            <Divider />

            {/* Brand Information */}
            <Box>
              <Text fontSize='lg' fontWeight='bold' mb={3} color='green.600'>
                Brand Information
              </Text>
              <VStack spacing={3} align='stretch'>
                <DetailRow
                  label='Brand'
                  value={
                    mappingDetails?.brand ? (
                      <VStack align='start' spacing={1}>
                        <Text fontWeight='medium' color='green.700'>
                          {mappingDetails.brand.name}
                        </Text>
                        <Badge colorScheme='blue' size='sm'>
                          ID: {mappingDetails.brand.id}
                        </Badge>
                      </VStack>
                    ) : (
                      <Text color='gray.500' fontStyle='italic'>
                        No brand mapped
                      </Text>
                    )
                  }
                  isLoading={isLoading}
                />
                <DetailRow
                  label='Brand Confidence'
                  value={
                    <Badge colorScheme='blue' variant='outline'>
                      {Math.round((mappingDetails?.brandConfidence || 0) * 100)}%
                    </Badge>
                  }
                  isLoading={isLoading}
                />
              </VStack>
            </Box>

            <Divider />

            {/* License Information */}
            <Box>
              <Text fontSize='lg' fontWeight='bold' mb={3} color='orange.600'>
                License Information
              </Text>
              <VStack spacing={3} align='stretch'>
                <DetailRow label='License Number' value={mappingDetails?.licenseNumber} isLoading={isLoading} />
                <DetailRow
                  label='License Confidence'
                  value={
                    <Badge colorScheme='green' variant='outline'>
                      {Math.round((mappingDetails?.licenseConfidence || 0) * 100)}%
                    </Badge>
                  }
                  isLoading={isLoading}
                />
                <DetailRow
                  label='Business Name'
                  value={mappingDetails?.dedDetail?.businessName || 'Not available'}
                  isLoading={isLoading}
                />
              </VStack>
            </Box>

            <Divider />
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
